import {useCallback, useEffect} from 'react';

import {useFormContext} from 'react-hook-form';
import {connect} from 'react-redux';

import {ApplicationStoreType} from 'desktop/store';
import {AddressFormItemType, AddressFormType} from 'desktop/types/addressType';

import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';

import {getDeliveryStep} from '../../store/selectors/deliveryStep/deliveryStep';

export type PropsType = {
    deliveryStep: ApplicationStoreType['checkoutSideReview']['deliveryStep'];
};

/**
 * This component updated react-hook-form state from redux
 * 1. When persisted data from localStorage received once
 * 2. When customer addresses changed and data transformation logic applied to redux state
 *
 * also see EditFormFromReduxUpdater which works for new address creation or existing edit
 */
export const FormFromReduxUpdater = ({deliveryStep}: PropsType) => {
    const {setValue} = useFormContext<AddressFormType>();
    const customer = useGetCustomerData();

    // eslint-disable-next-line no-underscore-dangle
    const isRehydratedDeliveryStep = deliveryStep?._persist?.rehydrated;

    const updateFormFromRedux = useCallback(
        (reduxState: ApplicationStoreType['checkoutSideReview']['deliveryStep']) => {
            const {billingAddress, shippingAddress} = reduxState;

            const dataToSet = [
                {
                    data: shippingAddress,
                    target: 'shippingAddress',
                },
                {
                    data: billingAddress,
                    target: 'billingAddress',
                },
            ] as const;

            dataToSet.forEach(({data, target}) => {
                Object.entries(data).forEach(([key, formData]) => {
                    setValue(`${target}.${key as keyof AddressFormItemType}`, formData?.value, {
                        shouldTouch: Boolean(formData?.value),
                        shouldValidate: Boolean(formData?.error),
                    });
                });
            });
        },
        [setValue],
    );

    useEffect(() => {
        if (isRehydratedDeliveryStep) updateFormFromRedux(deliveryStep);
        // we need to trigger useEffect only when delivery step is rehydrated
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRehydratedDeliveryStep]);

    useEffect(() => {
        if (customer) updateFormFromRedux(deliveryStep);
        // we need to trigger useEffect only when customerAddresses changes to avoid unnecessary rerenders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, deliveryStep.customerAddresses]);

    return null;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    deliveryStep: getDeliveryStep(store),
});

export const FormFromReduxUpdaterConnected = connect(mapStateToProps)(FormFromReduxUpdater);
